import type { Block, Narration, NarrationClip } from './types'
import { blockText } from './text'

// Narration timing. The author marks when some blocks start ("syncs to
// paragraphs"); everything else is estimated from text length between the
// known marks, so a piece with no marks at all still follows along roughly.

/** Blocks the narration reads, in order: headings, paragraphs, pull quotes, thoughts, scene lines. */
export function isNarrated(b: Block): boolean {
  if (b.type === 'text' || b.type === 'heading' || b.type === 'pullquote' || b.type === 'thought' || b.type === 'scene') {
    return blockText(b).trim().length > 0
  }
  return false
}

export interface TimelineEntry {
  id: string
  start: number
  end: number
  /** true when the author stamped this start; false when estimated. */
  marked: boolean
}

/**
 * Start/end seconds for every narrated block. Explicit marks win (clamped to
 * be non-decreasing); unmarked runs are spread across the gap between their
 * neighbouring marks in proportion to character count.
 */
export function buildTimeline(blocks: Block[], narration: Pick<Narration, 'duration' | 'marks'>): TimelineEntry[] {
  const read = blocks.filter(isNarrated)
  const duration = Math.max(0, narration.duration || 0)
  if (read.length === 0 || duration === 0) return []
  const weights = read.map((b) => Math.max(1, blockText(b).length))
  const marks = narration.marks ?? {}

  // Anchors: indices with explicit marks, plus virtual anchors at 0 and the end.
  const starts: Array<number | null> = read.map((b) => {
    const m = marks[b.id]
    return typeof m === 'number' && Number.isFinite(m) ? Math.min(Math.max(0, m), duration) : null
  })
  // Keep explicit marks monotonic.
  let floor = 0
  for (let i = 0; i < starts.length; i++) {
    const s = starts[i]
    if (s === null) continue
    starts[i] = Math.max(s, floor)
    floor = starts[i]!
  }

  const out: number[] = new Array(read.length)
  let i = 0
  while (i < read.length) {
    if (starts[i] !== null) {
      out[i] = starts[i]!
      i++
      continue
    }
    // Unmarked run [i, j): spread between the previous start and the next mark (or the end).
    let j = i
    while (j < read.length && starts[j] === null) j++
    const prevIdx = i - 1
    const from = prevIdx >= 0 ? out[prevIdx] : 0
    const to = j < read.length ? starts[j]! : duration
    // The previous block also needs its share of the gap when it exists.
    const runWeights = weights.slice(prevIdx >= 0 ? prevIdx : i, j)
    const total = runWeights.reduce((a, b) => a + b, 0)
    let t = from
    for (let k = prevIdx >= 0 ? prevIdx : i; k < j; k++) {
      if (k >= i) out[k] = t
      t += ((to - from) * weights[k]) / total
    }
    i = j
  }

  return read.map((b, k) => ({
    id: b.id,
    start: out[k],
    end: k + 1 < read.length ? Math.max(out[k], out[k + 1]) : duration,
    marked: starts[k] !== null,
  }))
}

/** The block being read at `time` seconds, or null before the first / with no timeline. */
export function activeBlockAt(timeline: TimelineEntry[], time: number): string | null {
  if (timeline.length === 0 || time < 0) return null
  let lo = 0
  let hi = timeline.length - 1
  if (time < timeline[0].start) return null
  while (lo < hi) {
    const mid = (lo + hi + 1) >> 1
    if (timeline[mid].start <= time) lo = mid
    else hi = mid - 1
  }
  return timeline[lo].id
}

/** Where to seek so the narration reads `blockId`. */
export function startOf(timeline: TimelineEntry[], blockId: string): number | null {
  return timeline.find((e) => e.id === blockId)?.start ?? null
}

/** Drop marks for blocks that no longer exist (after edits or a restore). */
export function pruneMarks(blocks: Block[], marks: Record<string, number>): Record<string, number> {
  const ids = new Set(blocks.map((b) => b.id))
  return Object.fromEntries(Object.entries(marks).filter(([id]) => ids.has(id)))
}

/** The narrated blocks' ids in reading order. */
export function narratedOrder(blocks: Block[]): string[] {
  return blocks.filter(isNarrated).map((b) => b.id)
}

// ── Narrations recorded in pieces ────────────────────────────────────────
// A piece narration is a list of clips, each starting at a column block and
// played back to back. Everything a single-file narration has (duration,
// marks) is derived from the clips, so the timeline above works unchanged:
// each clip's block starts exactly where the clip does, and the paragraphs
// read inside one clip are estimated by length as usual.

/** A narration recorded in pieces (a non-empty `clips`), rather than one file. */
export function isPieceNarration(n: Pick<Narration, 'clips'> | null | undefined): n is Narration & { clips: NarrationClip[] } {
  return !!n && Array.isArray(n.clips) && n.clips.length > 0
}

/** A clip's usable length in seconds (bad or missing durations count as 0). */
export function clipLength(c: Pick<NarrationClip, 'duration'>): number {
  return Number.isFinite(c.duration) && c.duration > 0 ? c.duration : 0
}

/** Seconds of all the clips together. */
export function totalDuration(clips: ReadonlyArray<Pick<NarrationClip, 'duration'>>): number {
  return clips.reduce((sum, c) => sum + clipLength(c), 0)
}

export interface ClipSpan {
  clip: NarrationClip
  /** Where the clip starts on the whole narration's timeline, in seconds. */
  start: number
  end: number
}

/** Each clip placed on one global timeline, in playing order. */
export function clipsTimeline(clips: NarrationClip[]): ClipSpan[] {
  let t = 0
  return clips.map((clip) => {
    const start = t
    t += clipLength(clip)
    return { clip, start, end: t }
  })
}

/**
 * Global `seconds` as a clip and an offset into it. A time on a boundary
 * belongs to the clip that starts there; past the end is the end of the last.
 */
export function clipAt(spans: ClipSpan[], seconds: number): { index: number; offset: number } | null {
  if (spans.length === 0) return null
  const t = Math.max(0, seconds)
  let index = 0
  for (let i = 0; i < spans.length; i++) {
    if (spans[i].start > t) break
    // Skip empty clips unless nothing else starts here.
    if (spans[i].end > spans[i].start || i === 0) index = i
  }
  const s = spans[index]
  return { index, offset: Math.min(Math.max(0, t - s.start), s.end - s.start) }
}

/** marks[blockId] = where that block's first clip starts. */
export function marksFromClips(clips: NarrationClip[]): Record<string, number> {
  const marks: Record<string, number> = {}
  for (const { clip, start } of clipsTimeline(clips)) {
    if (!(clip.blockId in marks)) marks[clip.blockId] = Math.round(start * 1000) / 1000
  }
  return marks
}

/** The narration a set of recorded pieces makes (see the Narration type). */
export function narrationFromClips(clips: NarrationClip[], now: () => Date = () => new Date()): Narration {
  const stamps = clips.map((c) => c.recordedAt).filter((s): s is string => !!s)
  const latest = stamps.sort().at(-1)
  return {
    src: '',
    duration: totalDuration(clips),
    recorded_at: latest ?? now().toISOString(),
    marks: marksFromClips(clips),
    clips: clips.map((c) => ({ ...c })),
  }
}

/**
 * What the timeline needs: a piece narration's duration and marks come from
 * its clips (so stale stored marks can't drift from the audio); a single
 * file's are as stored.
 */
export function narrationTiming(n: Pick<Narration, 'duration' | 'marks' | 'clips'>): Pick<Narration, 'duration' | 'marks'> {
  if (isPieceNarration(n)) return { duration: totalDuration(n.clips), marks: marksFromClips(n.clips) }
  return { duration: n.duration, marks: n.marks ?? {} }
}

/** `clips` with the clip `clipId` swapped for `replacement` (one clip, several, or none). */
export function replaceClip<C extends Pick<NarrationClip, 'id'>>(clips: C[], clipId: string, replacement: C | C[]): C[] {
  const at = clips.findIndex((c) => c.id === clipId)
  if (at < 0) return clips
  const add = Array.isArray(replacement) ? replacement : [replacement]
  return [...clips.slice(0, at), ...add, ...clips.slice(at + 1)]
}

/**
 * Where the piece that starts at `blockId` ends: the next block (in `order`,
 * the narrated blocks' ids in reading order) that another clip starts at, or
 * null when it runs to the end.
 */
export function pieceEnd(clips: Array<Pick<NarrationClip, 'blockId'>>, order: string[], blockId: string): string | null {
  const pos = new Map(order.map((id, i) => [id, i]))
  const at = pos.get(blockId)
  if (at === undefined) return null
  let next = Infinity
  for (const c of clips) {
    const i = pos.get(c.blockId)
    if (i !== undefined && i > at && i < next) next = i
  }
  return next === Infinity ? null : order[next]
}

/**
 * Record over a stretch of the piece: the clips starting in [fromId, untilId)
 * (untilId null = to the end) are replaced by `replacement`, which lands where
 * they were. Clips whose block is gone (not in `order`) stay where they are.
 */
export function spliceClips<C extends Pick<NarrationClip, 'blockId'>>(
  clips: C[],
  order: string[],
  fromId: string,
  untilId: string | null,
  replacement: C[],
): C[] {
  const pos = new Map(order.map((id, i) => [id, i]))
  const from = pos.get(fromId)
  if (from === undefined) return clips
  const until = untilId === null ? Infinity : (pos.get(untilId) ?? Infinity)
  const inRange = (c: C) => {
    const i = pos.get(c.blockId)
    return i !== undefined && i >= from && i < until
  }
  let at = clips.findIndex((c) => {
    const i = pos.get(c.blockId)
    return i !== undefined && i >= from
  })
  if (at < 0) at = clips.length
  return [...clips.slice(0, at), ...replacement, ...clips.slice(at).filter((c) => !inRange(c))]
}

/** The narrated block after `blockId` in `order` (the first one when blockId is null), or null at the end. */
export function nextInOrder(order: string[], blockId: string | null): string | null {
  if (blockId === null) return order[0] ?? null
  const i = order.indexOf(blockId)
  return i >= 0 && i + 1 < order.length ? order[i + 1] : null
}

/** A single-file narration from an uploaded file, keeping the old single file's marks that still fit when asked. */
export function fileNarration(
  a: { src: string; duration: number; peaks?: number[]; recordedAt: string },
  keepMarksFrom?: Narration | null,
): Narration {
  const old = keepMarksFrom && !isPieceNarration(keepMarksFrom) ? (keepMarksFrom.marks ?? {}) : {}
  const marks = Object.fromEntries(Object.entries(old).filter(([, s]) => !(a.duration > 0) || s <= a.duration))
  return { src: a.src, duration: a.duration, recorded_at: a.recordedAt, marks, ...(a.peaks ? { peaks: a.peaks } : {}) }
}
